import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Music, Share2, Headphones, Sparkles } from 'lucide-react';
import { PhoneMockup } from '../../components/PhoneMockup';

const SpotifyDemo = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const isDemo = new URLSearchParams(location.search).get('demo') === 'true';
  
  useEffect(() => {
    if (isDemo) {
      window.history.replaceState({}, '', '/qr/demo-spotify');
    }
  }, [isDemo]);

  const handleBackToDemos = () => {
    navigate('/');
    setTimeout(() => {
      const demosSection = document.getElementById('demos');
      if (demosSection) {
        const sectionIndex = 7; // Demos is the 8th section (0-indexed)
        window.scrollTo({
          top: sectionIndex * window.innerHeight,
          behavior: 'smooth'
        });
      }
    }, 100);
  };

  // Sample data with a classical music playlist
  const playlistData = {
    url: 'https://open.spotify.com/playlist/37i9dQZF1DWWEJlAGA9gs0',
    spotifyId: '37i9dQZF1DWWEJlAGA9gs0', // Classical Essentials playlist
    bookTitle: 'Der Rabe und andere Gedichte',
    author: 'Edgar Allan Poe',
    bookCover: '/images/der-rabe-und-andere-gedichte-taschenbuch-edgar-allan-poe.webp',
    description: 'Diese Playlist wurde sorgfältig zusammengestellt, um das Leseerlebnis der düsteren Gedichte von Edgar Allan Poe zu bereichern. Tauche ein in die musikalische Welt der Gothic-Romantik.'
  };

  const brandingData = {
    companyName: 'Max Mustermann GmbH',
    logoUrl: null,
    primaryColor: '#4F46E5',
    secondaryColor: '#7C3AED',
    textColor: '#FFFFFF',
    displayType: 'name_only' as const
  };

  const bgGradient = `linear-gradient(to bottom right, ${brandingData.primaryColor}, ${brandingData.secondaryColor})`;

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="mb-8 text-center">
          {isDemo && (
            <button
              onClick={handleBackToDemos}
              className="mb-4 text-blue-600 hover:text-blue-700 transition-colors text-sm"
            >
              ← Zurück zu Demos
            </button>
          )}
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Spotify Playlist Demo</h1>
          <p className="text-gray-600">So sieht Ihre Spotify-Integration auf dem Smartphone aus</p>
        </div>

        {/* Phone Mockup with Spotify Page */}
        <PhoneMockup className="mb-8">
          <div 
            className="min-h-screen relative"
            style={{ background: bgGradient }}
          >
            {/* Animated background particles */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute -top-20 -right-20 w-40 h-40 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
              <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-yellow-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
              <div className="absolute top-20 left-20 w-40 h-40 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
            </div>

            {/* Header */}
            <div className="relative z-10">
              <div className="container mx-auto px-4 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-white">
                    <span className="font-bold text-base">{brandingData.companyName}</span>
                  </div>
                  <div className="text-white/80 p-1">
                    <Share2 className="w-4 h-4" />
                  </div>
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div className="relative z-10 container mx-auto px-3 pb-4">
              <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-3 shadow-2xl">
                {/* Book Cover Section - Smaller and side by side on mobile */}
                <div className="flex items-start gap-3 mb-3">
                  <div className="relative flex-shrink-0">
                    <img
                      src={playlistData.bookCover}
                      alt={playlistData.bookTitle}
                      className="w-20 h-28 object-cover rounded-lg shadow-xl"
                    />
                    <div className="absolute -top-1 -right-1 bg-green-500 text-white rounded-full p-1 shadow-lg">
                      <Sparkles className="w-2 h-2" />
                    </div>
                  </div>
                  
                  <div className="text-white flex-1">
                    <h2 className="text-sm font-bold mb-0.5">
                      Entdecke die Playlist
                    </h2>
                    <p className="text-white/80 text-xs mb-1">zu "{playlistData.bookTitle}"</p>
                    <div className="flex items-center gap-1 text-white/60 text-xs">
                      <Headphones className="w-3 h-3" />
                      <span>Begleitende Playlist</span>
                    </div>
                  </div>
                </div>

                {/* Spotify Player Section */}
                <div>
                  <h1 className="text-base font-bold text-white mb-2 flex items-center justify-center gap-2">
                    <Music className="w-4 h-4 text-green-400" />
                    Spotify Playlist
                  </h1>

                  {/* Spotify Embed */}
                  <div className="bg-black/40 backdrop-blur-sm rounded-xl p-1 mb-3">
                    <iframe
                      src={`https://open.spotify.com/embed/playlist/${playlistData.spotifyId}?utm_source=generator&theme=0`}
                      width="100%"
                      height="152"
                      frameBorder="0"
                      allowFullScreen
                      allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                      loading="lazy"
                      className="rounded-lg"
                      style={{ minHeight: '152px' }}
                    />
                  </div>

                  {/* Description */}
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-2.5 mb-2.5">
                    <p className="text-white/90 text-xs leading-relaxed">
                      {playlistData.description}
                    </p>
                  </div>

                  {/* Action Button */}
                  <button className="w-full bg-green-500 text-white px-3 py-2 rounded-full font-medium text-xs flex items-center justify-center gap-1.5 shadow-lg">
                    <Music className="w-3.5 h-3.5" />
                    Auf Spotify anhören
                  </button>
                </div>

                {/* Footer */}
                <div className="mt-3 text-center">
                  <p className="text-white/60 text-xs">
                    Powered by {brandingData.companyName}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </PhoneMockup>

        {/* Demo Info */}
        {isDemo && (
          <div className="max-w-4xl mx-auto">
            <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
              <h3 className="text-lg font-semibold mb-2 text-blue-900">Demo-Hinweis</h3>
              <p className="text-sm text-blue-800 mb-2">
                Dies ist eine Demo einer Spotify-Integration mit dem Branding "Max Mustermann GmbH". 
                Die Seite zeigt eine Playlist zu "Der Rabe und andere Gedichte" von Edgar Allan Poe.
              </p>
              <p className="text-sm text-blue-800">
                Mit BeyondPages können Sie Spotify-Playlists als Buchsoundtracks erstellen. 
                Der QR-Code im Buch führt Ihre Leser direkt zu dieser musikalischen Begleitung.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SpotifyDemo;